import type { EmitEvent } from "@/domains/core"
import type { EventEmitter } from "@/domains/library"
import type { Address } from "viem"
import type { Config } from "wagmi"
import { weiValueToBigNumber } from "@/domains/core"
import { MinimalPoolStore } from "@/domains/minimal-pool"
import { approveWithdrawPairAllowance, withdrawLiquidity, withdrawLiquidityQuote } from "@/domains/on-chain"

type WithdrawLiquidityActionInteraction = {
  connectedAddress: Address
  chainId: number
  config: Config
  emitter: EventEmitter<EmitEvent>
  amountWei: bigint
  routerAddress: Address
  minimalPool: MinimalPoolStore
  deadline: number
  slippage: number
  onSuccess: () => void
}

export const withdrawLiquidityActionInteraction = async ({
  connectedAddress,
  chainId,
  config,
  emitter,
  amountWei,
  minimalPool,
  routerAddress,
  deadline,
  slippage,
  onSuccess,
}: WithdrawLiquidityActionInteraction) => {
  if (!connectedAddress) {
    return emitter.emit("error-not-connected", {
      title: "Not connected",
      description: "In order to interact with this app, you first must connect your wallet.",
      variant: "error",
    })
  }

  if (!minimalPool) {
    emitter.emit("error-invalid-minimal-pool", {
      title: "Invalid minimal pool",
      description: "Unable to unstake liquidity - minimal pool information is missing",
      variant: "error",
    })
    return
  }

  // --------------------------------------------------------------------------------
  // STEP ONE: Check if the user has enough balance in their wallet to withdraw
  if (minimalPool.onChainUserBalance < amountWei) {
    emitter.emit("error-insufficient-balance-lp", {
      title: "Insufficient balance",
      description: `You do not have enough ${minimalPool.displayName} in your wallet. You are trying to withdraw ${minimalPool.formatUnits(amountWei - minimalPool.onChainUserBalance)} more than available.`,
      variant: "error",
    })
    return
  }

  // --------------------------------------------------------------------------------
  // STEP TWO: Do we have enough approval on the router to withdraw?
  // Request the desired allowance
  if (minimalPool.onChainUserRouterAllowance < amountWei) {
    const pairApproveRequest = await approveWithdrawPairAllowance({
      account: connectedAddress,
      chainId,
      config,
      pairAddress: minimalPool.address.toViemAddress(),
      routerAddress,
      amount: amountWei,
    })

    if (!pairApproveRequest.success) {
      emitter.emit("error-approve-withdraw", {
        title: `Unable to approve ${minimalPool.displayName} allowance`,
        description: pairApproveRequest.error || "Unknown error",
        variant: "error",
      })
      return
    }
  }

  // --------------------------------------------------------------------------------
  // STEP THREE: Get a quote from the router to see what we would receive

  // const quoteRemove = await routerContract.methods
  // .quoteRemoveLiquidity(token0.address, token1.address, pair.stable, sendAmount)
  // .call()

  const withdrawLiquidityQuoteResult = await withdrawLiquidityQuote({
    account: connectedAddress,
    chainId,
    config,
    routerAddress,
    token0: minimalPool.token0,
    token1: minimalPool.token1,
    stable: minimalPool.isStable,
    amount: amountWei,
  })

  if (!withdrawLiquidityQuoteResult.success) {
    emitter.emit("error-fetch-quote", {
      title: `Unable to fetch  ${minimalPool.displayName} quote`,
      description: withdrawLiquidityQuoteResult.error || "Unknown error",
      variant: "error",
    })
    return
  }

  // if the withdraw would fail with either side returning a _zero_ value
  if (withdrawLiquidityQuoteResult.success) {
    if (withdrawLiquidityQuoteResult.token0.value === "0" || withdrawLiquidityQuoteResult.token1.value === "0") {
      emitter.emit("error-zero-quote", {
        title: "The quote returned a zero value for one of the tokens, please withdraw a larger amount.",
        description: `You would receive ${withdrawLiquidityQuoteResult.token0.value} ${minimalPool.token0.symbol} and ${withdrawLiquidityQuoteResult.token1.value} ${minimalPool.token1.symbol}`,
        variant: "error",
      })
      return
    }
  }

  // Lets get the params all ready to go
  // sendSlippage should be independent for creates/withdraws imo
  // Convert slippage to basis points (multiply by 100 for precision)
  // e.g., 0.5% slippage = 50 basis points, keep (10000 - 50) / 10000 = 9950 / 10000 of quoted amount
  const slippageBasisPoints = BigInt(Math.floor(slippage * 100))
  const slippageMultiplier = 10000n - slippageBasisPoints

  const sendAmount0Min = (withdrawLiquidityQuoteResult.token0.wei * slippageMultiplier) / 10000n
  const sendAmount1Min = (withdrawLiquidityQuoteResult.token1.wei * slippageMultiplier) / 10000n

  // STEP THREE: Withdraw the LP from the Router
  const withdrawLiquidityResult = await withdrawLiquidity({
    account: connectedAddress,
    chainId,
    config,
    routerAddress,
    token0Address: minimalPool.token0.address.toViemAddress(),
    token1Address: minimalPool.token1.address.toViemAddress(),
    stable: minimalPool.isStable,
    sendAmount: amountWei,
    sendAmount0Min,
    sendAmount1Min,
    deadline,
  })

  if (withdrawLiquidityResult.success) {
    onSuccess?.()
    emitter.emit("success-withdraw-liquidity", {
      title: `Withdraw  ${minimalPool.displayName}`,
      description: `Withdrew ${minimalPool.formatUnits(amountWei)} ${minimalPool.displayName} tokens`,
      variant: "success",
    })
  }

  if (withdrawLiquidityResult.success == false) {
    emitter.emit("error-withdraw-liquidity", {
      title: `Unable to withdraw ${minimalPool.formatUnits(amountWei)} from ${minimalPool.displayName}`,
      description: withdrawLiquidityResult.error || "Unknown error",
      variant: "error",
    })
  }

  return withdrawLiquidityResult
}
